import { useState, useEffect, useRef } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { usePreviousRouterState } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { KNOWLEDGE_HUB_NESTED_TYPE } from '../../common/constants.tsx';
import type { KnowledgeType } from '../../common/types/index.tsx';
import { calculateStack } from './utils/index.tsx';

export type UseNestingStackArgs = {
	connectStack: string[] | null;
	forgeStack: string[] | null;
	knowledgeType: KnowledgeType | null;
	isLegacyAutomationEnabled: boolean;
	isTeamManagedProject: boolean;
};

type UseNestingStackReturn = [string[], (stack: string[]) => void];

/**
 * NOTE: If returned `stack` value does not match the menu panel you're rendering atm (in your React component) - <MenuItem> will render nothing.
 * *Only* items inside <NestingMenuItem id="correspondingId">... will be rendered, nothing else.
 *
 * NOTE: projectKey is taken from current page url atm.
 */
export const useNestingStack = ({
	connectStack,
	forgeStack,
	knowledgeType,
	isLegacyAutomationEnabled,
	isTeamManagedProject,
}: UseNestingStackArgs): UseNestingStackReturn => {
	const [currentRouterState] = useRouter();
	const previousRouterState = usePreviousRouterState();
	const forcedRootStack = useRef<{ enabled: boolean; queueId?: string }>({
		enabled: true,
		queueId: undefined,
	});

	const lastStackRef = useRef<string[]>([]);

	const knowledgeNest = knowledgeType === KNOWLEDGE_HUB_NESTED_TYPE;

	const [stack, setStack] = useState<string[]>(() =>
		calculateStack({
			connectStack,
			forgeStack,
			currentRouterState,
			forceRootStack: true,
			knowledgeNest,
			previousState: previousRouterState,
			isLegacyAutomationEnabled,
			isTeamManagedProject,
			lastStackRef,
		}),
	);

	const { projectKey } = currentRouterState.match.params;
	// Reset to the initial behaviour when the servicedesk project key changes
	useEffect(() => {
		forcedRootStack.current = {
			enabled: true,
			queueId: undefined,
		};
	}, [projectKey]);

	useEffect(() => {
		// We do not need to do anything if the forced root stack is disabled
		if (!forcedRootStack.current.enabled) {
			return;
		}

		const { action, match, route } = currentRouterState;
		const { queueId } = match.params;

		// Set the initial queueId on the forced root stack, if it has not been set yet
		if (forcedRootStack.current.queueId == null && queueId != null) {
			forcedRootStack.current.queueId = queueId;
		}

		// Disable the forced root stack, when:
		// the user moves to another queue
		const hasChangedQueue =
			forcedRootStack.current.queueId != null && forcedRootStack.current.queueId !== queueId;
		const isNavigatingToTheSamePageAgain = action === 'PUSH' && !route.isRedirect;
		const isNavigatingFromGettingStartedPage =
			fg('gsp_production_rollout') && route.name === 'servicedesk-getting-started-page';
		if (hasChangedQueue || isNavigatingToTheSamePageAgain || isNavigatingFromGettingStartedPage) {
			forcedRootStack.current.enabled = false;
		}
	}, [currentRouterState]);

	useEffect(() => {
		const forceRootStack = forcedRootStack.current.enabled;

		const nextStack = calculateStack({
			connectStack,
			forgeStack,
			currentRouterState,
			forceRootStack,
			knowledgeNest,
			previousState: previousRouterState,
			isLegacyAutomationEnabled,
			isTeamManagedProject,
			lastStackRef,
		});

		setStack(nextStack);
	}, [
		connectStack,
		forgeStack,
		currentRouterState,
		isLegacyAutomationEnabled,
		isTeamManagedProject,
		knowledgeNest,
		previousRouterState,
	]);

	/*
        Making an exception for settings page.
        When clicking Back menu item in settings page we don't want to re-calculate the stack;
		therefore, no transition happens, and will let the page redirect load happen to the root page.
    */
	if (lastStackRef.current.includes('settings') && stack.length === 0) {
		return [lastStackRef.current, setStack];
	}

	if (String(stack) !== String(lastStackRef)) {
		lastStackRef.current = stack;
	}

	return [stack, setStack];
};
